export const playbooks = {
  page: {
    title: 'Playbooks',
    subtitle: 'Manage your playbooks and rules',
    createPlaybook: 'Create Playbook',
    import: 'Import',
    createWithAI: 'Create with AI',
    loadFailed: 'Failed to load playbooks',
    deleteSuccess: 'Playbook deleted successfully',
    deleteFailed: 'Failed to delete playbook',
  },
  tabs: {
    playbooks: 'Playbooks',
    workflows: 'Workflows',
  },
  workflows: {
    tab: 'Workflows',
    title: 'Workflows',
    description: 'Saved tasks you run again, for example "Review a supplier agreement against our standard". Run them from the chat with /.',
    create: 'New workflow',
    empty: 'No workflows yet',
    emptyHint: 'Save the instructions you give QAgent often, and share them with your organization.',
    name: 'Name',
    namePlaceholder: 'e.g. Supplier SaaS review',
    descriptionLabel: 'Description (optional)',
    instructions: 'Instructions',
    instructionsHint: 'What QAgent should do. Write {{document}} where the chosen document goes.',
    instructionsPlaceholder: 'Review {{document}} against our SaaS standard. Focus on liability caps, data protection and termination. List deviations by risk.',
    kind: 'Runs as',
    kinds: {
      prompt: 'Instructions only',
      review: 'Review a document',
      compare: 'Compare with a standard agreement',
      summarize: 'Summarize a document',
    } as Record<string, string>,
    playbook: 'Playbook (optional)',
    standardAgreement: 'Standard agreement (optional)',
    none: 'None',
    visibility: 'Who can use it',
    private: 'Only me',
    organization: 'Everyone in my organization',
    shared: 'Shared',
    by: (name: string) => `by ${name}`,
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Workflow saved',
    deleted: 'Workflow deleted',
    failed: 'Something went wrong',
    deleteTitle: 'Delete workflow',
    deleteDescription: (name: string) => `Delete "${name}"? People it is shared with can no longer run it.`,
  },
  sections: {
    privateTitle: 'Private Playbooks',
    privateDescription: 'Playbooks visible only to you',
    organizationTitle: 'Organization Playbooks',
    organizationDescription: 'Playbooks shared across your organization',
  },
  table: {
    name: 'Name',
    agreementType: 'Agreement Type',
    rules: 'Rules',
    visibility: 'Visibility',
    created: 'Created',
    activeBadge: (active: number, total: number) => `${active} / ${total} active`,
    approvedBadge: 'Approved',
    private: 'Private',
    organization: 'Organization',
    openMenu: 'Open menu',
    actions: 'Actions',
    viewDetails: 'View details',
    delete: 'Delete',
    filterPlaceholder: 'Filter playbooks...',
    noPrivate: 'No private playbooks found.',
    noOrganization: 'No organization playbooks found.',
    loading: 'Loading...',
    previous: 'Previous',
    next: 'Next',
  },
  deleteDialog: {
    title: 'Delete Playbook',
    description: (name: string) =>
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
    cancel: 'Cancel',
    confirm: 'Delete',
  },
  detail: {
    loading: 'Loading playbook...',
    notFound: 'Playbook not found',
    backToPlaybooks: 'Back to Playbooks',
    hideSource: 'Hide Source',
    showSource: 'Show Source',
    edit: 'Edit',
    delete: 'Delete',
    editTitle: 'Edit Playbook',
    editDescription: 'Update playbook details',
    nameLabel: 'Name',
    namePlaceholder: 'Playbook name',
    agreementTypeLabel: 'Agreement Type',
    agreementTypePlaceholder: 'Agreement type',
    privateLabel: 'Private Playbook',
    privateOnlyYou: 'Only you can see this playbook',
    privateEveryone: 'Everyone in your organization can see this playbook',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    nameAndTypeRequired: 'Name and agreement type are required',
    updateSuccess: 'Playbook updated successfully',
    updateFailed: 'Failed to update playbook',
    deleteSuccess: 'Playbook deleted successfully',
    deleteFailed: 'Failed to delete playbook',
    rulesTitle: 'Rules',
    rulesDescription: 'Review rules defined in this playbook',
    addRule: 'Add Rule',
    noRules: 'No rules defined yet.',
    addFirstRule: 'Click "Add Rule" to create your first rule.',
    sourceDocumentTitle: 'Source Document',
    sourceDocumentDescription: (type: string) => `${type} document`,
    sourceDocumentPages: (count: number) => ` - ${count} pages`,
    sourceDocumentSheets: (count: number) => ` - ${count} sheets`,
    loadingSourceDocument: 'Loading source document...',
    noSourceDocument: 'No source document available',
    loadSourceFileFailed: 'Failed to load source document file',
    loadSourceError: 'Error loading source document',
  },
  createDialog: {
    title: 'Create Playbook',
    description:
      'Create a new playbook manually. You can add rules after creating the playbook.',
    nameLabel: 'Name',
    namePlaceholder: 'Enter playbook name',
    agreementTypeLabel: 'Agreement Type',
    agreementTypePlaceholder: 'e.g., NDA, Employment Contract, SaaS Agreement',
    privateLabel: 'Private Playbook',
    privateOnlyYou: 'Only you can see this playbook',
    privateEveryone: 'Everyone in your organization can see this playbook',
    cancel: 'Cancel',
    creating: 'Creating...',
    submit: 'Create Playbook',
    requiredFields: 'Please fill in all required fields',
    createSuccess: 'Playbook created successfully',
    createFailed: 'Failed to create playbook',
  },
  aiDialog: {
    title: 'Create Playbook with AI',
    description:
      'Describe the type of playbook you need, and AI will generate it with relevant rules.',
    stages: {
      reading: 'Reading your description',
      identifying: 'Identifying agreement type',
      drafting: 'Drafting review rules',
      refining: 'Refining clauses',
      almostDone: 'Almost done',
    },
    timeHint: 'This usually takes 10-25 seconds. Hang tight.',
    descriptionLabel: 'Description',
    descriptionPlaceholder:
      'Example: Create a playbook for reviewing employment contracts. Include rules for compensation clauses, termination conditions, and confidentiality agreements.',
    descriptionHint:
      'Be specific about the agreement type and what rules you want included.',
    privateLabel: 'Private Playbook',
    privateOnlyYou: 'Only you can see this playbook',
    privateEveryone: 'Everyone in your organization can see this playbook',
    cancel: 'Cancel',
    generating: 'Generating...',
    submit: 'Generate Playbook',
    describeRequired: 'Please describe the playbook you want to create',
    createSuccess: 'Playbook created successfully with AI',
    createFailed: 'Failed to create playbook with AI',
  },
  importDialog: {
    title: 'Import Playbook',
    description:
      'Upload a document file to automatically generate a playbook using AI. Supported formats: PDF, DOCX, XLSX, XLS, TXT (max 25MB).',
    fileLabel: 'Document File',
    dropHint: 'Click to select a file or drag and drop',
    formatsHint: 'PDF, DOCX, XLSX, XLS, TXT (max 25MB)',
    selectFile: 'Select File',
    uploadingProcessing: 'Uploading and processing...',
    longDocumentHint: 'Long documents can take a few minutes. Keep this window open.',
    privateLabel: 'Private Playbook',
    privateOnlyYou: 'Only you can see this playbook',
    privateEveryone: 'Everyone in your organization can see this playbook',
    cancel: 'Cancel',
    importing: 'Importing...',
    submit: 'Import Playbook',
    fileSizeExceeds: (size: string) =>
      `File size exceeds 25MB limit. Your file is ${size}MB.`,
    invalidFileType: 'Invalid file type. Accepted formats: PDF, DOCX, XLSX, XLS, TXT',
    selectFileRequired: 'Please select a file to import',
    importSuccess: 'Playbook imported successfully!',
    importFailed: 'Failed to import playbook',
    unexpectedError: 'An unexpected error occurred during import',
  },
  createRuleDialog: {
    title: 'Create Rule',
    description: 'Add a new rule to this playbook.',
    nameLabel: 'Rule Name',
    namePlaceholder: 'e.g., Check compensation clause',
    instructionsLabel: 'Instructions',
    instructionsPlaceholder: 'Describe what this rule should check or validate...',
    enableCommentsLabel: 'Enable Comments',
    enableCommentsHint: 'Allow AI to add comments when reviewing this rule',
    commentGuidelinesLabel: 'Comment Guidelines (Optional)',
    commentGuidelinesPlaceholder:
      'Provide guidelines for how AI should comment on this rule...',
    activeLabel: 'Active Rule',
    activeHint: 'Only active rules are used during document review',
    cancel: 'Cancel',
    creating: 'Creating...',
    submit: 'Create Rule',
    requiredFields: 'Please fill in all required fields',
    createSuccess: 'Rule created successfully',
    createFailed: 'Failed to create rule',
  },
  ruleItem: {
    inactive: 'Inactive',
    comments: 'Comments',
    sources: (count: number) => `${count} source${count > 1 ? 's' : ''}`,
    nameLabel: 'Name',
    namePlaceholder: 'Rule name',
    instructionsLabel: 'Instructions',
    instructionsPlaceholder: 'Rule instructions',
    enableCommentsLabel: 'Enable Comments',
    enableCommentsHint: 'Allow AI to add comments during review',
    commentGuidelinesLabel: 'Comments Guidelines (Optional)',
    commentGuidelinesPlaceholder: 'Guidelines for how AI should comment on this rule',
    activeLabel: 'Active Rule',
    activeHint: 'Only active rules are used during review',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    instructionsHeading: 'Instructions',
    commentGuidelinesHeading: 'Comment Guidelines',
    sourceQuotesHeading: 'Source Quotes',
    paragraph: (index: number) => `Paragraph ${index}`,
    page: (page: number) => `Page ${page}`,
    sheetCell: (sheet: string, cell: string) => `Sheet: ${sheet}, Cell: ${cell}`,
    confidence: (level: string) => `${level} confidence`,
    created: (date: string) => `Created: ${date}`,
    updated: (date: string) => `Updated: ${date}`,
    deleteConfirm: 'Are you sure you want to delete this rule?',
    nameAndInstructionsRequired: 'Name and instructions are required',
    updateSuccess: 'Rule updated successfully',
    updateFailed: 'Failed to update rule',
    deleteSuccess: 'Rule deleted successfully',
    deleteFailed: 'Failed to delete rule',
    activated: 'Rule activated',
    deactivated: 'Rule deactivated',
  },
  viewer: {
    noSourceDocument: 'No source document available',
    unsupportedFileType: (type: string) => `Unsupported file type: ${type}`,
    errorLoadingPdf: (error: string) => `Error loading PDF: ${error}`,
    loadingPdf: 'Loading PDF...',
    pageOf: (page: number, total: number) => `Page ${page} of ${total}`,
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    resetZoom: 'Reset zoom',
    fitToWidth: 'Fit to width',
    loadingDocument: 'Loading document...',
    loadingSpreadsheet: 'Loading spreadsheet...',
    error: (error: string) => `Error: ${error}`,
  },
};

export type PlaybooksDict = typeof playbooks;
